/**
 * Collection USP bar (Figma 1:745).
 *
 * Mobile/tablet (below 1024px) always shows the moving ticker, regardless
 * of item count — there's no Figma spec for that width, and a scrolling
 * ticker reads better than 3 short lines wrapped on a narrow screen.
 *
 * Desktop (1024px+) matches Figma exactly: with the default 3 items
 * (data-usp-loop="false") it's fully static — laid out edge-to-edge by
 * CSS, no motion. Once a 4th+ item is added (data-usp-loop="true") it
 * becomes an infinite auto-scroll ticker, same mechanism as
 * assets/announcement-bar.js: the "lap" (items + a spacer) is cloned once
 * so the track can scroll left forever and wrap seamlessly. Items always
 * keep their natural (unstretched) size and gap. The spacer after them is
 * sized so the lap (items + spacer) is always at least one viewport wide,
 * so the clone's items never become visible while the original lap is
 * still on screen.
 *
 * Used by: sections/collection-usp-v2.liquid
 */
(function () {
  const initUsp = (bar) => {
    if (!bar || bar.dataset.uspInitialized === 'true') return;
    bar.dataset.uspInitialized = 'true';

    const viewport = bar.querySelector('.lp-collection-usp-v2__viewport');
    const track = bar.querySelector('.lp-collection-usp-v2__track');
    const originalLap = track?.querySelector('.lp-collection-usp-v2__lap');
    if (!viewport || !track || !originalLap) return;

    const duration = (Number(bar.dataset.scrollDuration) || 20) * 1000;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion) return;

    const loopEnabled = bar.dataset.uspLoop === 'true';
    const desktopQuery = window.matchMedia('(min-width: 1024px)');
    // Below 1024px, always animate. At 1024px+, only animate when there
    // are more than 3 items — matches the CSS's own `--static` breakpoint.
    const isStaticNow = () => !loopEnabled && desktopQuery.matches;

    let rafId = null;
    let offset = 0;
    let speed = 0;
    let lastTime = null;
    let paused = false;
    let originalWidth = 0;

    const MIN_GAP = 64;

    const getSetNaturalWidth = (set) => {
      const items = [...set.querySelectorAll('.lp-collection-usp-v2__item')];
      if (items.length === 0) return 0;
      const gap = parseFloat(getComputedStyle(set).gap) || 0;
      let width = 0;
      items.forEach((item, index) => {
        width += item.offsetWidth;
        if (index > 0) width += gap;
      });
      return width;
    };

    // Keeps the spacer just wide enough that set + spacer >= one viewport
    // width, so the next lap's items are still off-screen when this lap's
    // last item exits — regardless of how narrow the 3 items are relative
    // to the screen.
    const setSpacerWidths = () => {
      const viewportWidth = viewport.clientWidth;
      track.querySelectorAll('.lp-collection-usp-v2__lap').forEach((lap) => {
        const set = lap.querySelector('.lp-collection-usp-v2__set');
        const spacer = lap.querySelector('.lp-collection-usp-v2__spacer');
        if (!set || !spacer) return;
        const naturalWidth = getSetNaturalWidth(set);
        const width = Math.max(MIN_GAP, viewportWidth - naturalWidth + MIN_GAP);
        spacer.style.width = `${width}px`;
      });
    };

    const ensureClone = () => {
      if (track.dataset.cloned === 'true') return;
      const clone = originalLap.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a').forEach((link) => {
        link.tabIndex = -1;
      });
      track.appendChild(clone);
      track.dataset.cloned = 'true';
    };

    const applyOffset = () => {
      track.style.transform = `translate3d(${offset}px, 0, 0)`;
    };

    const measure = ({ preserveOffset = false } = {}) => {
      const previousWidth = originalWidth;
      const previousOffset = offset;

      setSpacerWidths();
      ensureClone();
      originalWidth = originalLap.offsetWidth;

      if (isStaticNow() || originalWidth <= 0) {
        speed = 0;
        offset = 0;
        applyOffset();
        return;
      }

      speed = originalWidth / duration;

      if (preserveOffset && previousWidth > 0) {
        offset = (previousOffset / previousWidth) * originalWidth;
        while (offset <= -originalWidth) offset += originalWidth;
        while (offset > 0) offset -= originalWidth;
      } else {
        offset = 0;
      }

      applyOffset();
    };

    measure();

    const onResize = () => measure({ preserveOffset: true });
    window.addEventListener('resize', onResize);

    const onMouseEnter = () => {
      paused = true;
    };
    const onMouseLeave = () => {
      paused = false;
      lastTime = null;
    };
    viewport.addEventListener('mouseenter', onMouseEnter);
    viewport.addEventListener('mouseleave', onMouseLeave);

    // Keyboard users get the same pause as mouse hover — without it, a
    // tabbed-into link keeps sliding out from under focus.
    const onFocusIn = () => {
      paused = true;
    };
    const onFocusOut = () => {
      paused = false;
      lastTime = null;
    };
    viewport.addEventListener('focusin', onFocusIn);
    viewport.addEventListener('focusout', onFocusOut);

    track.querySelectorAll('img').forEach((image) => {
      if (!image.complete) {
        image.addEventListener('load', () => measure({ preserveOffset: true }), { once: true });
      }
    });

    const tick = (time) => {
      if (speed > 0 && lastTime !== null && !paused) {
        const delta = time - lastTime;
        offset -= speed * delta;
        if (offset <= -originalWidth) offset += originalWidth;
        applyOffset();
      }
      lastTime = time;
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);

    bar._uspCleanup = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
      viewport.removeEventListener('mouseenter', onMouseEnter);
      viewport.removeEventListener('mouseleave', onMouseLeave);
      viewport.removeEventListener('focusin', onFocusIn);
      viewport.removeEventListener('focusout', onFocusOut);
      delete bar.dataset.uspInitialized;
      delete bar._uspCleanup;
    };
  };

  document.querySelectorAll('[data-collection-usp]').forEach(initUsp);

  document.addEventListener('shopify:section:load', (event) => {
    const bar = event.target.querySelector('[data-collection-usp]');
    if (bar) initUsp(bar);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelector('[data-collection-usp]')?._uspCleanup?.();
  });
})();
